'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppStoreButtons from '@/components/common/AppStoreButtons';

interface HeroSectionProps {
  className?: string;
}

const HeroSection = ({ className = '' }: HeroSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeUsers, setActiveUsers] = useState(50000);
  const [confidence, setConfidence] = useState(87);
  const [winRate, setWinRate] = useState(67);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const animateCounter = (
      target: number,
      setter: (value: number) => void,
      duration: number = 2000
    ) => {
      const start = 0;
      const increment = target / (duration / 16);
      let current = start;

      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          setter(target);
          clearInterval(timer);
        } else {
          setter(Math.floor(current));
        }
      }, 16);

      return () => clearInterval(timer);
    };

    const cleanup1 = animateCounter(50000, setActiveUsers);
    const cleanup2 = animateCounter(87, setConfidence);
    const cleanup3 = animateCounter(67, setWinRate);

    return () => {
      cleanup1();
      cleanup2();
      cleanup3();
    };
  }, [isHydrated]);

  return (
    <section className={`relative w-full min-h-screen flex items-center justify-center overflow-hidden ${className}`}>
      <div className="absolute inset-0 bg-gradient-to-br from-background via-card to-background opacity-50" />
      
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex flex-col items-center text-center space-y-8">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-card border border-primary/20 rounded-full">
            <Icon name="SparklesIcon" size={20} className="text-primary" variant="solid" />
            <span className="text-sm font-medium text-foreground">AI-Powered Trading Signals</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold bg-gradient-to-r from-primary via-primary to-secondary bg-clip-text text-transparent leading-tight max-w-4xl">
            AI-driven market signals — simple, continuous, effective.
          </h1>

          {/* Subheading */}
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl">
            Join 50,000+ traders using AI-driven signals with 87% confidence rate. 
            Start trading smarter, not harder, with real-time insights powered by advanced algorithms.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <AppStoreButtons />
            <Link
              href="/app-download"
              className="w-full sm:w-auto px-8 py-4 text-base font-heading font-semibold text-foreground bg-card border border-border rounded-lg hover:bg-muted transition-all duration-200 flex items-center justify-center space-x-2"
            >
              <Icon name="ChartBarIcon" size={20} />
              <span>Learn More</span>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        {isHydrated && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-12 max-w-4xl mx-auto">
            <div className="bg-card border border-border rounded-xl p-6 hover:shadow-cyan-glow transition-all duration-200 text-center">
              <div className="text-4xl font-heading font-bold text-primary mb-2">
                {activeUsers.toLocaleString()}+
              </div>
              <div className="text-sm text-muted-foreground">Active Traders</div>
            </div>
            <div className="bg-card border border-border rounded-xl p-6 hover:shadow-cyan-glow transition-all duration-200 text-center">
              <div className="text-4xl font-heading font-bold text-success mb-2">
                {confidence}%
              </div>
              <div className="text-sm text-muted-foreground">Confidence Rate</div>
            </div>
            <div className="bg-card border border-border rounded-xl p-6 hover:shadow-cyan-glow transition-all duration-200 text-center">
              <div className="text-4xl font-heading font-bold text-accent mb-2">
                {winRate}%
              </div>
              <div className="text-sm text-muted-foreground">Win Rate</div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroSection;